import { i18n, TFunction } from 'i18next';

import { DetailedRoute } from '@/generated/model/detailedRoute';
import { dateMDE } from '@/utilities/dateUtils';

export class RouteGrouper {
  private static readonly oneDayMillis: number = 86_400_000;
  private i18n: i18n;
  private t: TFunction;

  constructor(i18n: i18n, t: TFunction) {
    this.i18n = i18n;
    this.t = t;
  }

  groupRoutesByTime(routes: DetailedRoute[], startDate: Date, endDate: Date) {
    let lastStartDate = this.getInitialStartDate(routes, startDate);
    const routesByTimes = routes.reduce((acc: { [key: string]: (DetailedRoute | undefined)[] }, route) => {
      const startDate = this.getStartDate(route);
      const endDate = this.getEndDate(route);
      const dateKey = this.createDateKey(startDate, endDate);
      if (!acc[dateKey]) {
        this.addEmptyDayIfNeeded(lastStartDate, startDate, acc);
        acc[dateKey] = [];
      }
      acc[dateKey].push(route);
      lastStartDate = endDate;
      return acc;
    }, {});
    this.addEmptyDayIfNeeded(lastStartDate, this.truncateToPlusOneDay(endDate), routesByTimes);
    return routesByTimes;
  }

  private getInitialStartDate(routes: DetailedRoute[], startDate: Date) {
    const truncatedStartDate = this.truncateToMinusOneDay(startDate);
    if (!routes.length) {
      return truncatedStartDate;
    }
    const routeStartDate = this.getStartDate(routes[0]);
    if (routeStartDate.getTime() < truncatedStartDate.getTime()) {
      return routeStartDate;
    }
    return truncatedStartDate;
  }

  private truncateToPlusOneDay(date: Date) {
    const newDate = new Date(date.getTime() + RouteGrouper.oneDayMillis);
    return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
  }

  private truncateToMinusOneDay(date: Date) {
    const newDate = new Date(date.getTime() - RouteGrouper.oneDayMillis);
    return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
  }

  private getStartDate(route: DetailedRoute) {
    const date = new Date(route.startPosition.timeMicros / 1000);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private getEndDate(route: DetailedRoute) {
    const date = new Date(route.stopPosition.timeMicros / 1000);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private createDateKey(startDate: Date, endDate: Date) {
    if (startDate.getTime() == endDate.getTime()) {
      return dateMDE(startDate, this.i18n.language, this.t);
    }
    const startKey = dateMDE(startDate, this.i18n.language, this.t);
    const endKey = dateMDE(endDate, this.i18n.language, this.t);
    return startKey + ' -> ' + endKey;
  }

  private addEmptyDayIfNeeded(
    lastStartDate: Date,
    startDate: Date,
    acc: { [key: string]: (DetailedRoute | undefined)[] }
  ) {
    const startTime = startDate.getTime();
    if (lastStartDate.getTime() == startTime || lastStartDate.getTime() + RouteGrouper.oneDayMillis == startTime) {
      // simple day change without gap
      return;
    }
    const days = this.getTimeDiffInDays(lastStartDate, startDate);
    if (days == 2) {
      // only one day gap, just one date shown
      const dateKey = dateMDE(
        new Date(lastStartDate.getTime() + RouteGrouper.oneDayMillis),
        this.i18n.language,
        this.t
      );
      acc[dateKey] = [undefined];
    } else {
      const dateKeyStart = dateMDE(
        new Date(lastStartDate.getTime() + RouteGrouper.oneDayMillis),
        this.i18n.language,
        this.t
      );
      const dateKeyEnd = dateMDE(new Date(startTime - RouteGrouper.oneDayMillis), this.i18n.language, this.t);
      const dateKey = dateKeyStart + ' - ' + dateKeyEnd;
      acc[dateKey] = [undefined];
    }
  }

  private getTimeDiffInDays(lastStartDate: Date, startDate: Date) {
    const diffInMilliseconds = Math.abs(lastStartDate.getTime() - startDate.getTime());
    return diffInMilliseconds / RouteGrouper.oneDayMillis;
  }
}
